import { getDropdownOptions } from '@/constants/fieldOptions'
import { getFieldInputType } from '@/constants/vehicleFieldConfig'
import { isUsableEnhancementValue } from '@/utils/addVehicleEnhancementPrefill'
import { normalizeBooleanLike } from '@/utils/vehicleHistoryBooleanFields'
import {
  isFutureDealerDateInputValue,
  isValidDateInputValue,
  normalizeIntegerInput,
  toDealerDateInputValue,
} from '@/utils/vehicleFieldValueNormalization'

export type EnhancementSourceId = 'chrome'

export const CHROME_ENHANCEMENT_SOURCE = {
  id: 'chrome' as EnhancementSourceId,
  label: 'Chrome' as const,
}

export type EnhancementSourceLoadStatus = 'idle' | 'loading' | 'loaded' | 'error'

export type EnhancementFieldInputType =
  | 'number'
  | 'text'
  | 'textarea'
  | 'date'
  | 'boolean'
  | 'select'

export type EnhancementFieldCandidate = {
  source: EnhancementSourceId
  sourceLabel: 'Chrome'
  field: string
  value: unknown
}

export type PreparedEditVehicleEnhancementCandidate =
  | {
      applicable: true
      candidate: EnhancementFieldCandidate
      inputType: EnhancementFieldInputType
      preparedValue: unknown
    }
  | {
      applicable: false
    }

const DEALER_DATE_INPUT = /^\d{4}-\d{2}-\d{2}$/
const CLEAN_NUMERIC_STRING = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/
const MISSING_CURRENT_DISPLAY = '—'

export type CurrentEnhancementControlStatus = 'missing' | 'valid' | 'invalid'

export type CurrentEnhancementControlAssessment = {
  status: CurrentEnhancementControlStatus
  normalizedValue: unknown
  displayValue: string
}

function isBlankCommittedValue(value: unknown): boolean {
  if (value == null) return true
  if (typeof value === 'string' && value.trim() === '') return true
  return false
}

function isScalarEnhancementValue(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
}

/**
 * Same numeric commit rule Edit Vehicle uses for draft-backed number fields.
 * A non-finite result is not an applicable source candidate.
 */
function prepareNumberValue(field: string, value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null

  if (field === 'passenger_capacity') {
    const normalized = normalizeIntegerInput(value)
    return normalized == null ? null : normalized
  }

  const parsed = parseFloat(String(value))
  if (!Number.isFinite(parsed)) return null
  return parsed
}

function prepareTextValue(value: unknown): string | null {
  if (!isScalarEnhancementValue(value)) return null
  if (typeof value === 'string') return value
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null
  return value ? 'true' : 'false'
}

function prepareDateValue(value: unknown, dealerTimeZone?: string): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const projected = toDealerDateInputValue(value, dealerTimeZone)
  if (!DEALER_DATE_INPUT.test(projected) || !isValidDateInputValue(projected)) return null
  return projected
}

function prepareBooleanValue(value: unknown): boolean | null {
  if (!isScalarEnhancementValue(value)) return null
  const normalized = normalizeBooleanLike(value)
  return normalized === true || normalized === false ? normalized : null
}

function prepareSelectValue(field: string, value: unknown): string | null {
  if (typeof value !== 'string') return null
  const options = getDropdownOptions(field)
  if (!options?.length) return null

  const exact = options.find((option) => option.value === value)
  if (exact) return exact.value

  const folded = value.trim().toLowerCase()
  if (!folded) return null

  const byValue = options.find((option) => option.value.trim().toLowerCase() === folded)
  if (byValue) return byValue.value

  const byTitle = options.find((option) => option.title.trim().toLowerCase() === folded)
  if (byTitle) return byTitle.value

  return null
}

export function formatPreparedEnhancementValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'string') return value
  return ''
}

function missingCurrentAssessment(): CurrentEnhancementControlAssessment {
  return {
    status: 'missing',
    normalizedValue: undefined,
    displayValue: MISSING_CURRENT_DISPLAY,
  }
}

function validCurrentAssessment(
  normalizedValue: string | number | boolean,
): CurrentEnhancementControlAssessment {
  return {
    status: 'valid',
    normalizedValue,
    displayValue: formatPreparedEnhancementValue(normalizedValue),
  }
}

function formatUnusableCurrentValue(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (value == null) return MISSING_CURRENT_DISPLAY
  return String(value)
}

function invalidCurrentAssessment(
  value: unknown,
  normalizedValue?: unknown,
): CurrentEnhancementControlAssessment {
  return {
    status: 'invalid',
    normalizedValue,
    displayValue: formatUnusableCurrentValue(value),
  }
}

function isStructuredCurrentValue(value: unknown): boolean {
  return typeof value === 'object' && value !== null
}

function parseCleanNumericString(value: string): number | null {
  const trimmed = value.trim()
  if (!CLEAN_NUMERIC_STRING.test(trimmed)) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Current checkbox state. A displayed boolean is always valid, including false.
 * Null, undefined, and unrecognized stored values display as false.
 */
function assessBooleanCurrentValue(value: unknown): CurrentEnhancementControlAssessment {
  if (typeof value === 'boolean') return validCurrentAssessment(value)
  return validCurrentAssessment(normalizeBooleanLike(value) ?? false)
}

function assessNumberCurrentValue(
  field: string,
  value: unknown,
): CurrentEnhancementControlAssessment {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return missingCurrentAssessment()
  }
  if (isStructuredCurrentValue(value) || typeof value === 'boolean') {
    return invalidCurrentAssessment(value)
  }

  if (field === 'passenger_capacity') {
    const normalized = normalizeIntegerInput(value)
    if (normalized == null) return invalidCurrentAssessment(value)
    return validCurrentAssessment(normalized)
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return invalidCurrentAssessment(value)
    return validCurrentAssessment(value)
  }

  if (typeof value === 'string') {
    const parsed = parseCleanNumericString(value)
    if (parsed == null) return invalidCurrentAssessment(value)
    return validCurrentAssessment(parsed)
  }

  return invalidCurrentAssessment(value)
}

function assessTextCurrentValue(value: unknown): CurrentEnhancementControlAssessment {
  if (value == null) return missingCurrentAssessment()
  if (isStructuredCurrentValue(value)) return invalidCurrentAssessment(value)
  if (typeof value === 'string') {
    if (value.trim() === '') return missingCurrentAssessment()
    return validCurrentAssessment(value)
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return invalidCurrentAssessment(value)
    return validCurrentAssessment(String(value))
  }
  if (typeof value === 'boolean') return validCurrentAssessment(value ? 'true' : 'false')
  return invalidCurrentAssessment(value)
}

function assessDateCurrentValue(
  field: string,
  value: unknown,
  dealerTimeZone?: string,
): CurrentEnhancementControlAssessment {
  if (value == null || typeof value !== 'string') {
    if (value == null) return missingCurrentAssessment()
    return invalidCurrentAssessment(value)
  }

  const trimmed = value.trim()
  if (!trimmed) return missingCurrentAssessment()
  if (!DEALER_DATE_INPUT.test(trimmed) || !isValidDateInputValue(trimmed)) {
    return invalidCurrentAssessment(value)
  }
  if (field === 'date_in_stock_ts' && isFutureDealerDateInputValue(trimmed, dealerTimeZone)) {
    return invalidCurrentAssessment(trimmed, trimmed)
  }
  return validCurrentAssessment(trimmed)
}

function assessSelectCurrentValue(
  field: string,
  value: unknown,
): CurrentEnhancementControlAssessment {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return missingCurrentAssessment()
  }
  if (typeof value !== 'string') return invalidCurrentAssessment(value)

  const resolved = prepareSelectValue(field, value)
  if (!resolved) return invalidCurrentAssessment(value)
  return validCurrentAssessment(resolved)
}

/**
 * Assess the current Edit Vehicle control value.
 * Callers pass the displayed control value. This does not prepare a source candidate.
 */
export function assessCurrentEnhancementControlValue(params: {
  field: string
  value: unknown
  dealerTimeZone?: string
}): CurrentEnhancementControlAssessment {
  const inputType = getFieldInputType(params.field)
  if (inputType === 'number') return assessNumberCurrentValue(params.field, params.value)
  if (inputType === 'boolean') return assessBooleanCurrentValue(params.value)
  if (inputType === 'date') {
    return assessDateCurrentValue(params.field, params.value, params.dealerTimeZone)
  }
  if (inputType === 'select') return assessSelectCurrentValue(params.field, params.value)
  return assessTextCurrentValue(params.value)
}

export function prepareEditVehicleEnhancementCandidate(params: {
  field: string
  sanitizedValues: Record<string, unknown> | null | undefined
  dealerTimeZone?: string
}): PreparedEditVehicleEnhancementCandidate {
  const field = params.field
  const values = params.sanitizedValues
  if (!values || !Object.prototype.hasOwnProperty.call(values, field)) {
    return { applicable: false }
  }

  const value = values[field]
  if (!isUsableEnhancementValue(value)) return { applicable: false }

  const inputType = getFieldInputType(field)
  const candidate: EnhancementFieldCandidate = {
    source: CHROME_ENHANCEMENT_SOURCE.id,
    sourceLabel: CHROME_ENHANCEMENT_SOURCE.label,
    field,
    value,
  }

  let preparedValue: unknown
  if (inputType === 'number') {
    preparedValue = prepareNumberValue(field, value)
    if (preparedValue == null || isBlankCommittedValue(preparedValue)) return { applicable: false }
  } else if (inputType === 'text' || inputType === 'textarea') {
    preparedValue = prepareTextValue(value)
    if (preparedValue == null || isBlankCommittedValue(preparedValue)) return { applicable: false }
  } else if (inputType === 'date') {
    preparedValue = prepareDateValue(value, params.dealerTimeZone)
    if (preparedValue == null) return { applicable: false }
  } else if (inputType === 'boolean') {
    preparedValue = prepareBooleanValue(value)
    if (preparedValue == null) return { applicable: false }
  } else if (inputType === 'select') {
    preparedValue = prepareSelectValue(field, value)
    if (preparedValue == null) return { applicable: false }
  } else {
    return { applicable: false }
  }

  return {
    applicable: true,
    candidate,
    inputType,
    preparedValue,
  }
}
